import { useAppSelector } from '@customer/store/hooks';
import { ReactElement, Ref } from 'react';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { MultiselectFilters } from '@customer/store/slices/filtersSlice';
import Field from '@cas/components/inputs/field/Field';
import Skeleton from 'react-loading-skeleton';
import DropdownMultiselect from '@cas/components/inputs/dropdown-multiselect/DropdownMultiselect';
import { DropdownItemProps } from '@cas/components/inputs/dropdown-items/DropdownItems';
import useDropdownMultiselect from '@customer/components/inputs/dropdown-multiselect/useDropdownMultiselect';

interface MultiselectFilterProps<T> {
  filterName: keyof MultiselectFilters;
  label: ReactElement | string;
  elementRef: Ref<HTMLVdsBasicDropdownMultiselectElement>;
  action: ActionCreatorWithPayload<T[]>;
  disabledOnEmpty?: boolean;
  useItemsHook: () => DropdownItemProps<T>[];
}

export const MultiselectFilter = <T,>({
  filterName,
  label,
  elementRef,
  action,
  disabledOnEmpty,
  useItemsHook,
}: MultiselectFilterProps<T>) => {
  const value: T[] = useAppSelector(state => state.filters.draft[filterName]) as T[];

  const { items, onChange } = useDropdownMultiselect<T>({
    useItemsHook,
    action,
  });

  return (
    <Field uid={filterName} label={label}>
      {items.length || disabledOnEmpty ? (
        <DropdownMultiselect
          elementRef={elementRef}
          items={items}
          onChange={onChange}
          uid={filterName}
          value={value}
          disabled={disabledOnEmpty && !items.length}
        />
      ) : (
        <Skeleton width="100%" height={34} />
      )}
    </Field>
  );
};

export default MultiselectFilter;
